import { defineAsyncComponent, defineComponent, h, ref, SetupContext } from 'vue'
import { WdsModal } from '@worksome/wds-components'
import goFetch from '@/utils/goFetch'
import { NetworkError } from '@/utils/notification'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { buildContractDrawerUrl } from '@/components/contractDrawerUrl'

interface Props {
  hireId: string
  action: string
  loading: boolean
  drawer?: boolean
  contractId?: string
}

enum Emits {
  UPDATE_LOADING = 'update:loading',
  CLOSE = 'close',
}

const loadingCache = {}
// We have to keep a record of the emit functions as they may change when opening / closing the modal and
// therefore need to be overwritten
const emitFuncs: Record<string, (event: string, ...args: any[]) => void> = {}
const contractAnimationSpeedMs = 50

const createContract = (hireId: string, action: string, drawer: boolean, contractId?: string) =>
  defineAsyncComponent({
    async loader() {
      const id = atob(hireId).split(':')[1]
      const contractLocalId = contractId ? atob(contractId).split(':')[1] : undefined
      const cacheKey = `${hireId}-${action}${contractId ? `-${contractId}` : ''}`
      loadingCache[cacheKey] = true
      try {
        emitFuncs[hireId](Emits.UPDATE_LOADING, true)
        const root = document.location.origin

        const url = buildContractDrawerUrl(root, action, id, contractLocalId)

        const res = await goFetch(url, true)
        loadingCache[cacheKey] = false
        emitFuncs[hireId](Emits.UPDATE_LOADING, false)

        // Create a custom html environment to manipulate our html into a usable state
        const el = document.createElement('html')

        // Here we grab relevant information form the html and remove unneeded elements
        el.innerHTML = res
        const title = el.querySelector('.modal-title')?.textContent
        el.getElementsByTagName('script')[0].remove()
        el.querySelector('.modal-header')?.remove()

        // Applying custom styling
        el.querySelectorAll('.modal-content').forEach((modalEl) => {
          const e = modalEl as HTMLElement
          e.style['box-shadow'] = 'none'
          e.style.border = 'none'
        })
        const e = el.querySelector('.modal-body') as HTMLElement
        e.style['padding-top'] = '0 !important'

        // We need to add our events here before letting vue parse the html
        const event = `@close="cancel"`
        const innerHtml = el.getElementsByTagName('body')[0].innerHTML
        const index = innerHtml.indexOf('<contract-form') + '<contract-form'.length
        const html = `${innerHtml.slice(0, index)} ${event} ${innerHtml.slice(index)}`

        return defineComponent({
          components: {
            WdsModal,
          },
          setup() {
            const show = ref(false)
            setTimeout(() => {
              show.value = true
            }, contractAnimationSpeedMs)
            const cancel = () => {
              show.value = false
              setTimeout(() => emitFuncs[hireId](Emits.CLOSE), contractAnimationSpeedMs)
            }

            return { show, cancel }
          },
          template: `
          <div v-if="${drawer}" class="contract-drawer">
            <div class="contract-content" @cancel="cancel">
              ${html}
            </div>
          </div>
          <teleport v-else to="body">
            <WdsModal :show="show" title="${title}" @cancel="cancel" custom-size="640px">
              <div class="modal-dialog" role="document" id="contract-summary-modal-content" style="margin: 0;">
                ${html}
              </div>
            </WdsModal>
          </teleport>
      `,
        })
      } catch (ex: any) {
        emitFuncs[hireId](Emits.UPDATE_LOADING, false)
        const e = new NetworkError({ ex, msg: $t('Failed to load contract') })
        e.dispatch()

        throw ex
      }
    },
  })

const cache = {}
const Contract = (props: Props, { emit }: SetupContext) => {
  const hireId = props['hire-id']
  const { action } = props
  const drawer = props.drawer ?? false
  const contractId = props['contract-id']
  const cacheKey = `${hireId}-${action}${contractId ? `-${contractId}` : ''}`

  // We set the new emit function
  emitFuncs[hireId] = emit

  if (cache[cacheKey]) {
    // We emit the current loading state of the component
    emit(Emits.UPDATE_LOADING, loadingCache[cacheKey])
    return cache[cacheKey]
  }

  // We create a new asynchronous component
  const comp = h(createContract(hireId, action, drawer, contractId))
  cache[cacheKey] = comp
  return comp
}

export default Contract
